import { type LiveChatQueryResponse, type LiveChatItem } from 'app/hooks/fetch/useFetchLiveChat';
import BaseChatItem from 'app/modules/virtual-assistant/components/chat-items/BaseChatItem';
import { addLinkData } from 'app/utils/addLinkData';
import { linkify } from 'app/utils/linkify';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import useLiveChatBotAvatar from '../hooks/useLiveChatBotAvatar';
import LiveChatMessageForm from './LiveChatMessageForm';
import MessageBubble from './MessageBubble';

export default function MessageFactory({
  item,
  index,
  array,
  liveChat,
  onClickConsentLink,
  onSubmitMessageForm,
  onSkipMessageForm,
  isLoadingMessageForm,
  prevMessageSource,
}: {
  item: LiveChatItem;
  index: number;
  array: LiveChatItem[];
  liveChat?: LiveChatQueryResponse;
  onClickConsentLink: (e: React.MouseEvent<HTMLDivElement>) => void;
  onSubmitMessageForm: (
    data: {
      [k: string]: FormDataEntryValue;
    },
    form: LiveChatItem
  ) => void;
  onSkipMessageForm: (
    data: {
      [k: string]: FormDataEntryValue;
    },
    form: LiveChatItem
  ) => void;
  isLoadingMessageForm: boolean;
  prevMessageSource?: string;
}) {
  const avatarURL = useLiveChatBotAvatar();
  const prevItem = array[index - 1];
  const itemTypes = ['hide_previous_messages'];
  const hideAvatar =
    prevItem &&
    ((prevItem.type === 'form' && !prevItem.completed && !prevItem.dismissed) ||
      itemTypes.includes(prevItem.type) ||
      prevItem.source === item.source);

  const getSpacing = () => {
    if (prevMessageSource === item.source) {
      return 'mt-6 mb-0';
    } else {
      return 'mt-8 mb-0';
    }
  };

  switch (item.type) {
    case 'message': {
      if (!item.message) {
        return null;
      }

      const hasH1Tag = /<h1>.+<\/h1>/gim.test(item.message) && !liveChat?.consentMessage?.completed;

      return (
        <BaseChatItem
          messageId={item.static_ref_id}
          avatarURL={avatarURL}
          showAvatar={!hideAvatar}
          className={twMerge(
            '[&_h1]:(pb-4 leading-6 font-semibold)',
            hasH1Tag ? 'flex-col [&_h1]:text-xl' : 'flex-row [&_h1]:text-sm',
            getSpacing()
          )}
        >
          <MessageBubble
            onClick={onClickConsentLink}
            dangerouslySetInnerHTML={{
              __html: linkify(addLinkData(item.additional_info, item.message)),
            }}
          />
        </BaseChatItem>
      );
    }

    case 'form': {
      return (
        <BaseChatItem
          showAvatar={false}
          className={getSpacing()}
        >
          <MessageBubble
            isAuthor={item.completed || item.dismissed}
            key={index}
          >
            <LiveChatMessageForm
              form={item}
              isLoading={isLoadingMessageForm}
              onSubmit={onSubmitMessageForm}
              onSkip={onSkipMessageForm}
            />
          </MessageBubble>
        </BaseChatItem>
      );
    }
    default: {
      return null;
    }
  }
}
